import React, { useContext } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { number, shape, string } from 'prop-types';
import CheckboxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import * as R from 'ramda';

import { CreateReconsignmentContext } from '../Create';

const SourceLocationCard = props => {
  const { sourceLocation } = props;
  const { consignmentStatus, fromLocation, inventoryStatus, origin, productStatus, productVolume } =
    sourceLocation;
  const { locationId, locationName } = fromLocation;
  const { originName } = origin;

  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });

  const { info, setInfo, setValidation } = useContext(CreateReconsignmentContext);
  const { sourceLocations } = info;

  const isSelected =
    sourceLocations.filter(
      location => R.path(['fromLocation', 'locationId'], location) === locationId
    ).length > 0;

  // check if is recurring
  const isRecurring = R.prop('isRecurring', info);

  const handleChange = () => {
    isRecurring
      ? setInfo(
          R.evolve(
            {
              fromLocations: R.always([]),
              sourceLocations: isSelected ? R.always([]) : R.always([sourceLocation]),
              toLocations: R.always([]),
            },
            info
          )
        )
      : setInfo(
          R.evolve(
            {
              fromLocations: R.always([]),
              sourceLocations: isSelected
                ? R.always(
                    R.filter(
                      location => R.path(['fromLocation', 'locationId'], location) !== locationId,
                      sourceLocations
                    )
                  )
                : R.always([...sourceLocations, sourceLocation]),
              toLocations: R.always([]),
            },
            info
          )
        );
    setValidation({});
  };

  return (
    <Box
      onClick={handleChange}
      sx={{
        bgcolor: 'surface.white',
        columnGap: 2,
        cursor: 'pointer',
        display: 'grid',
        gridAutoFlow: { lg: 'column' },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        gridTemplateColumns: { xs: 'auto repeat(2, 1fr)', lg: 'auto 1fr auto repeat(4, 1fr)' },
        gridTemplateRows: { lg: 'repeat(2, 1fr)' },
        mb: 2,
        p: 3,
        rowGap: 0.5,
      }}
    >
      <Checkbox
        checked={isSelected}
        checkedIcon={<CheckboxRoundedIcon sx={{ color: 'secondary.main' }} />}
        onChange={handleChange}
        sx={{
          '&.MuiCheckbox-root': {
            p: 0,
          },
          gridRow: '1/3',
          justifyContent: 'flex-start',
        }}
      />
      {/* eslint-disable-next-line sort-keys-fix/sort-keys-fix */}
      <Typography sx={{ gridColumn: { xs: '2/4', lg: 'auto/auto' } }} variant="body1bold">
        {locationName}
      </Typography>
      <Typography
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        sx={{ fontWeight: 400, gridColumn: { xs: '2/4', lg: 'auto/auto' } }}
        variant="caption"
      >
        Volume: <span className="css-heap-redact"> {productVolume} </span>
      </Typography>
      {!lgUp && (
        <Typography
          sx={{
            fontWeight: 400,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            gridColumn: { xs: '2/4', lg: 'auto/auto' },
          }}
          variant="caption"
        >
          Origin: {originName}
        </Typography>
      )}
      {lgUp && (
        <>
          <Divider flexItem orientation="vertical" sx={{ gridRow: '1/3' }} />
          <Typography
            sx={{
              fontWeight: 400,
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridColumn: { xs: '2/3', lg: 'auto/auto' },
            }}
            variant="caption"
          >
            Inventory Status
          </Typography>
          <Typography
            sx={{
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridColumn: { xs: '3/4', lg: 'auto/auto' },
            }}
            variant="body2"
          >
            {inventoryStatus}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridColumn: { xs: '2/3', lg: 'auto/auto' },
            }}
            variant="caption"
          >
            Controlled Status
          </Typography>
          <Typography
            sx={{
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridColumn: { xs: '3/4', lg: 'auto/auto' },
            }}
            variant="body2"
          >
            {consignmentStatus}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridColumn: { xs: '2/3', lg: 'auto/auto' },
            }}
            variant="caption"
          >
            Reserved Status
          </Typography>
          <Typography
            sx={{
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridColumn: { xs: '3/4', lg: 'auto/auto' },
            }}
            variant="body2"
          >
            {productStatus}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridColumn: { xs: '2/3', lg: 'auto/auto' },
            }}
            variant="caption"
          >
            Origin
          </Typography>
          <Typography
            sx={{
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridColumn: { xs: '3/4', lg: 'auto/auto' },
            }}
            variant="body2"
          >
            {originName}
          </Typography>
        </>
      )}
    </Box>
  );
};

SourceLocationCard.propTypes = {
  sourceLocation: shape({
    consignmentStatus: string,
    fromLocation: shape({
      locationId: number,
      locationName: string,
    }),
    inventoryStatus: string,
    origin: shape({
      originName: string,
    }),
    productVolume: number,
  }).isRequired,
};

export default SourceLocationCard;
